import type { BaseUniversalId, PermanentId, UniversalId } from "./dentition";

// Universal numbering: permanent 1-32, then primary A-J upper and K-T lower. Kept apart from
// dentition.tsx, which pulls every tooth image in with it.
export const PERMANENT_TEETH = Array.from({ length: 32 }, (_, i) => String(i + 1));
export const PRIMARY_TEETH = Array.from({ length: 20 }, (_, i) => String.fromCharCode(65 + i));
const TOOTH_INDEX = new Map([...PERMANENT_TEETH, ...PRIMARY_TEETH].map((tooth, i) => [tooth, i]));

export function sortTeeth(teeth: Iterable<string>): string[] {
  const rank = (tooth: string) => TOOTH_INDEX.get(tooth) ?? Number.MAX_SAFE_INTEGER;
  return [...new Set(teeth)].sort((a, b) => rank(a) - rank(b));
}

export type Quadrant = 1 | 2 | 3 | 4;

export const QUADRANT_TEETH: Record<Quadrant, readonly PermanentId[]> = {
  1: ["1", "2", "3", "4", "5", "6", "7", "8"], // upper right
  2: ["9", "10", "11", "12", "13", "14", "15", "16"], // upper left
  3: ["17", "18", "19", "20", "21", "22", "23", "24"], // lower left
  4: ["25", "26", "27", "28", "29", "30", "31", "32"], // lower right
} as const;

export function isQuadrantMaxillary(quadrant: Quadrant): boolean {
  return quadrant === 1 || quadrant === 2;
}

export function isQuadrantRight(quadrant: Quadrant): boolean {
  return quadrant === 1 || quadrant === 4;
}

export function getQuadrantForTooth(universal: UniversalId): Quadrant {
  if (universal >= "A" && universal <= "E") return 1;
  if (universal >= "F" && universal <= "J") return 2;
  if (universal >= "K" && universal <= "O") return 3;
  if (universal >= "P" && universal <= "T") return 4;

  const num = parseInt(universal, 10);
  if (num >= 1 && num <= 8) return 1;
  if (num >= 9 && num <= 16) return 2;
  if (num >= 17 && num <= 24) return 3;
  return 4;
}

// Average tooth size in millimetres: `length` is crown plus root, `width` is the crown measured
// mesial to distal. Wheeler's Dental Anatomy (permanent: the carving table; primary: Black's
// measurements). The chart draws every tooth at one scale from these, so a canine stands taller
// than a molar and a lower incisor is narrower than an upper one.
type ToothSize = { length: number; width: number };

const UPPER_CENTRAL: ToothSize = { length: 23.5, width: 8.5 };
const UPPER_LATERAL: ToothSize = { length: 22, width: 6.5 };
const UPPER_CANINE: ToothSize = { length: 27, width: 7.5 };
const UPPER_PREMOLAR: ToothSize = { length: 22.5, width: 7 };
const UPPER_FIRST_MOLAR: ToothSize = { length: 20, width: 10 };
const UPPER_SECOND_MOLAR: ToothSize = { length: 18.5, width: 9 };
const UPPER_THIRD_MOLAR: ToothSize = { length: 17.5, width: 8.5 };
const LOWER_CENTRAL: ToothSize = { length: 21.5, width: 5 };
const LOWER_LATERAL: ToothSize = { length: 23.5, width: 5.5 };
const LOWER_CANINE: ToothSize = { length: 27, width: 7 };
const LOWER_PREMOLAR: ToothSize = { length: 22.5, width: 7 };
const LOWER_FIRST_MOLAR: ToothSize = { length: 21.5, width: 11 };
const LOWER_SECOND_MOLAR: ToothSize = { length: 20, width: 10.5 };
const LOWER_THIRD_MOLAR: ToothSize = { length: 18, width: 10 };
const PRIMARY_UPPER_CENTRAL: ToothSize = { length: 16, width: 6.5 };
const PRIMARY_UPPER_LATERAL: ToothSize = { length: 15.8, width: 5.1 };
const PRIMARY_UPPER_CANINE: ToothSize = { length: 19, width: 7 };
const PRIMARY_UPPER_FIRST_MOLAR: ToothSize = { length: 15.2, width: 7.3 };
const PRIMARY_UPPER_SECOND_MOLAR: ToothSize = { length: 17.5, width: 8.2 };
const PRIMARY_LOWER_CENTRAL: ToothSize = { length: 14, width: 4.2 };
const PRIMARY_LOWER_LATERAL: ToothSize = { length: 15, width: 4.1 };
const PRIMARY_LOWER_CANINE: ToothSize = { length: 17, width: 5 };
const PRIMARY_LOWER_FIRST_MOLAR: ToothSize = { length: 15.8, width: 7.7 };
const PRIMARY_LOWER_SECOND_MOLAR: ToothSize = { length: 18.8, width: 9.9 };

const TOOTH_SIZES: Record<BaseUniversalId, ToothSize> = {
  "1": UPPER_THIRD_MOLAR,
  "2": UPPER_SECOND_MOLAR,
  "3": UPPER_FIRST_MOLAR,
  "4": UPPER_PREMOLAR,
  "5": UPPER_PREMOLAR,
  "6": UPPER_CANINE,
  "7": UPPER_LATERAL,
  "8": UPPER_CENTRAL,
  "9": UPPER_CENTRAL,
  "10": UPPER_LATERAL,
  "11": UPPER_CANINE,
  "12": UPPER_PREMOLAR,
  "13": UPPER_PREMOLAR,
  "14": UPPER_FIRST_MOLAR,
  "15": UPPER_SECOND_MOLAR,
  "16": UPPER_THIRD_MOLAR,
  "17": LOWER_THIRD_MOLAR,
  "18": LOWER_SECOND_MOLAR,
  "19": LOWER_FIRST_MOLAR,
  "20": LOWER_PREMOLAR,
  "21": LOWER_PREMOLAR,
  "22": LOWER_CANINE,
  "23": LOWER_LATERAL,
  "24": LOWER_CENTRAL,
  "25": LOWER_CENTRAL,
  "26": LOWER_LATERAL,
  "27": LOWER_CANINE,
  "28": LOWER_PREMOLAR,
  "29": LOWER_PREMOLAR,
  "30": LOWER_FIRST_MOLAR,
  "31": LOWER_SECOND_MOLAR,
  "32": LOWER_THIRD_MOLAR,
  A: PRIMARY_UPPER_SECOND_MOLAR,
  B: PRIMARY_UPPER_FIRST_MOLAR,
  C: PRIMARY_UPPER_CANINE,
  D: PRIMARY_UPPER_LATERAL,
  E: PRIMARY_UPPER_CENTRAL,
  F: PRIMARY_UPPER_CENTRAL,
  G: PRIMARY_UPPER_LATERAL,
  H: PRIMARY_UPPER_CANINE,
  I: PRIMARY_UPPER_FIRST_MOLAR,
  J: PRIMARY_UPPER_SECOND_MOLAR,
  K: PRIMARY_LOWER_SECOND_MOLAR,
  L: PRIMARY_LOWER_FIRST_MOLAR,
  M: PRIMARY_LOWER_CANINE,
  N: PRIMARY_LOWER_LATERAL,
  O: PRIMARY_LOWER_CENTRAL,
  P: PRIMARY_LOWER_CENTRAL,
  Q: PRIMARY_LOWER_LATERAL,
  R: PRIMARY_LOWER_CANINE,
  S: PRIMARY_LOWER_FIRST_MOLAR,
  T: PRIMARY_LOWER_SECOND_MOLAR,
};

// The longest tooth, which sets the height of a tooth row.
export const LONGEST_TOOTH_MM = UPPER_CANINE.length;

// Column widths are deliberately flatter than the teeth. Each tooth's surface selector sits in the
// tooth's own column and is the same size for every tooth, so every column weighs at least as much
// as an 8mm tooth, and a wider tooth gets only part of its extra width so the molars don't take
// the room back from the front teeth. The teeth themselves are still drawn to scale.
const NARROWEST_COLUMN_WEIGHT = 8;
const EXTRA_WIDTH_SHARE = 0.6;

export function toothLengthMm(universal: BaseUniversalId): number {
  return TOOTH_SIZES[universal].length;
}

export function toothColumnWeight(universal: BaseUniversalId): number {
  const extra = Math.max(0, TOOTH_SIZES[universal].width - NARROWEST_COLUMN_WEIGHT);
  return NARROWEST_COLUMN_WEIGHT + extra * EXTRA_WIDTH_SHARE;
}
